import i18n from "@/i18n";
/**
 * PeriodCloseControl — Prompt 17B2.
 *
 * Compact manager surface for Period Close / Reopen. Renders next to
 * the Period navigation so the manager can:
 *   • see the current Close state (Open / Closed / Reopened)
 *   • Close the period            → POST /api/workforce/periods/{id}/close
 *   • Reopen a closed period      → POST /api/workforce/periods/{id}/reopen
 *   • Close again after Reopen    → same POST /close (version N+1)
 *
 * The component owns the small dialogs (Close confirm, Reopen reason)
 * and the manager-safe error mapping documented by 17B2 §14 · §16 ·
 * §17 · §22. It NEVER exposes internal IDs / raw service codes.
 *
 * Visibility is gated by `authorized` (Workforce admin role set). The
 * backend remains the final enforcement (§18).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { formatYMDDate } from "@/components/workforce/workforceHelpers";

// Prompt 17B2 §14 · §15 · §16 · §17 — verbatim manager-facing copy.
export const _CLOSE_CTA = "Close period";
export const _CLOSE_AGAIN_CTA = "Close again";
export const _REOPEN_CTA = "Reopen";
export const _CLOSE_DIALOG_TITLE = "Close this period?";
export const _CLOSE_DIALOG_BODY =
    "Once closed, schedule and worked-hour data for this period "
    + "cannot be edited unless the period is reopened.";
export const _CLOSE_DIALOG_CONFIRM = "Close period";
export const _CLOSE_DIALOG_CANCEL = "Cancel";
export const _REOPEN_DIALOG_TITLE = "Reopen this period?";
export const _REOPEN_DIALOG_BODY =
    "Enter the reason for reopening. Managers will see this in the "
    + "period's history.";
export const _REOPEN_REASON_LABEL = "Reason for reopening";
export const _REOPEN_DIALOG_CONFIRM = "Reopen period";
export const _REOPEN_DIALOG_CANCEL = "Cancel";

export const _MSG_UNVERIFIED =
    "Some worked hours still need review before this period can be "
    + "closed.";
export const _MSG_ALREADY_CLOSED = "This period is already closed.";
export const _MSG_ALREADY_OPEN = "This period is already open.";
export const _MSG_REOPEN_UNAVAILABLE =
    "Reopen is temporarily unavailable. Try again.";
export const _MSG_CLOSE_GENERIC = "Could not close the period. Try again.";
export const _MSG_REOPEN_GENERIC = "Could not reopen the period. Try again.";

// Prompt 17B2 §18 — visibility mirrors the backend's authorized set.
export const PERIOD_CLOSE_AUTHORIZED_ROLES = new Set([
    "restaurant", "restaurant_admin", "corporate",
]);

// Prompt 17B2 §9 — reason bounds match the canonical backend rule
// (3–500 chars after trim). The client only prevents obvious empty
// submits; backend remains authoritative.
const _REASON_MIN = 3;
const _REASON_MAX = 500;

function _isClosed(state) {
    return state && state.current_state === "CLOSED";
}
function _hasHistory(state) {
    return Boolean(state && state.latest_close_version
        && state.latest_close_version >= 1);
}
function _formatClosedAt(iso) {
    // Best-effort DD.MM.YYYY; falls back to a plain date if the
    // response ever changes shape. NEVER exposes an ISO string.
    if (!iso || typeof iso !== "string") return "";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    if (!m) return "";
    return formatYMDDate(`${m[1]}-${m[2]}-${m[3]}`);
}

export default function PeriodCloseControl({
    restaurantId,
    periodId,
    user,
    onStateChanged,
}) {
    const authorized = !!(
        user && PERIOD_CLOSE_AUTHORIZED_ROLES.has(user.role));

    // Prompt 17B2 §13 — canonical hydration via GET /close.
    const [state, setState] = useState(null); // null = loading / unknown
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(false);
    const [closeDialogOpen, setCloseDialogOpen] = useState(false);
    const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
    const [reason, setReason] = useState("");
    const reqIdRef = useRef(0);
    // Stable emitter — the parent-supplied `onStateChanged` is
    // typically an inline arrow, so referencing it directly from a
    // memoized loader would trigger an effect loop. Keep the latest
    // callback in a ref and expose a stable wrapper.
    const onStateChangedRef = useRef(onStateChanged);
    useEffect(() => {
        onStateChangedRef.current = onStateChanged;
    }, [onStateChanged]);

    const _emit = useCallback((next) => {
        const fn = onStateChangedRef.current;
        if (typeof fn === "function") fn(next);
    }, []);

    // Prompt 17B2 §13 · §22 — canonical read. 404 with
    // `WORKFORCE_PERIOD_NOT_CLOSED` means the period has never been
    // closed (no history). Any other error is treated as unknown so
    // controls remain hidden (fail-closed for visibility).
    const load = useCallback(async () => {
        if (!restaurantId || !periodId) {
            setState(null);
            _emit(null);
            return;
        }
        const myReq = ++reqIdRef.current;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/workforce/periods/${periodId}/close`,
                { params: { restaurant_id: restaurantId } },
            );
            if (myReq !== reqIdRef.current) return;
            // Prompt 17B2 §13 · §22 — trust `current_state` when
            // present. If the response is malformed / empty we fall
            // back to OPEN (no history) rather than defaulting to
            // CLOSED, so a stale or unrelated 200 cannot silently
            // lock the page.
            const cs = data && typeof data.current_state === "string"
                ? data.current_state : "OPEN";
            const lv = Number(
                (data && (data.latest_close_version
                    || data.close_version)) || 0);
            const next = {
                current_state: cs,
                latest_close_version: lv,
                closed_at: (data && data.closed_at) || "",
                averaging_validity: (data && data.averaging_validity) || null,
                standard_validity: (data && data.standard_validity) || null,
            };
            setState(next);
            _emit(next);
        } catch (err) {
            if (myReq !== reqIdRef.current) return;
            const code = err && err.response && err.response.data
                && err.response.data.detail
                && err.response.data.detail.code;
            if (code === "WORKFORCE_PERIOD_NOT_CLOSED") {
                const next = {
                    current_state: "OPEN",
                    latest_close_version: 0,
                    closed_at: "",
                };
                setState(next);
                _emit(next);
            } else {
                setState(null);
                _emit(null);
            }
        } finally {
            if (myReq === reqIdRef.current) setLoading(false);
        }
    }, [restaurantId, periodId, _emit]);

    // Prompt 17B2 §19 — refresh when restaurant/location or period
    // changes so stale state cannot leak across contexts.
    useEffect(() => { load(); }, [load]);

    // R5 · R6 — after Close / Reopen, merge only the read-only Averaging
    // validity from GET /close; the POST result stays the state source.
    const refreshValidity = useCallback(async (next) => {
        try {
            const { data } = await api.get(
                `/workforce/periods/${periodId}/close`,
                { params: { restaurant_id: restaurantId } });
            if (!data || (!data.averaging_validity && !data.standard_validity)) return;
            const merged = {
                ...next,
                averaging_validity: data.averaging_validity || null,
                standard_validity: data.standard_validity || null,
            };
            setState(merged);
            _emit(merged);
        } catch (_e) { /* validity is advisory; Close state already set */ }
    }, [restaurantId, periodId, _emit]);

    // Prompt 17B2 §5 · §10 — Close (or Close again) handler.
    const doClose = useCallback(async () => {
        if (!restaurantId || !periodId || busy) return;
        setBusy(true);
        try {
            const { data } = await api.post(
                `/workforce/periods/${periodId}/close`, null,
                { params: { restaurant_id: restaurantId } },
            );
            const next = {
                current_state: "CLOSED",
                latest_close_version: Number(
                    data.latest_close_version || data.close_version || 1),
                closed_at: data.closed_at || "",
            };
            setState(next);
            _emit(next);
            refreshValidity(next);
            setCloseDialogOpen(false);
            toast.success(
                `Period closed — version ${next.latest_close_version}`);
        } catch (err) {
            const status = err && err.response
                ? err.response.status : undefined;
            const detail = err && err.response && err.response.data
                ? err.response.data.detail : undefined;
            const code = detail && typeof detail === "object"
                ? detail.code : undefined;
            if (status === 409
                    && code === "WORKFORCE_PERIOD_UNVERIFIED_ACTUALS") {
                // §14 — actionable readiness blocker.
                toast.error(i18n.t("wf.PeriodCloseControl._MSG_UNVERIFIED", _MSG_UNVERIFIED));
                setCloseDialogOpen(false);
                await load();
            } else if (status === 409
                    && code === "WORKFORCE_PERIOD_ALREADY_CLOSED") {
                // §15 — stale UI. Reconcile.
                toast.error(i18n.t("wf.PeriodCloseControl._MSG_ALREADY_CLOSED", _MSG_ALREADY_CLOSED));
                setCloseDialogOpen(false);
                await load();
            } else {
                toast.error(i18n.t("wf.PeriodCloseControl._MSG_CLOSE_GENERIC", _MSG_CLOSE_GENERIC));
                setCloseDialogOpen(false);
            }
        } finally {
            setBusy(false);
        }
    }, [restaurantId, periodId, busy, load, _emit, refreshValidity]);

    // Prompt 17B2 §8 · §9 · §10 · §17 — Reopen handler.
    const doReopen = useCallback(async () => {
        if (!restaurantId || !periodId || busy) return;
        const trimmed = (reason || "").trim();
        if (trimmed.length < _REASON_MIN
                || trimmed.length > _REASON_MAX) {
            // §9 — client-side prevention. Backend remains authoritative.
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post(
                `/workforce/periods/${periodId}/reopen`,
                { reason: trimmed },
                { params: { restaurant_id: restaurantId } },
            );
            const next = {
                current_state: "OPEN",
                latest_close_version: Number(
                    data.latest_close_version || 0),
                closed_at: "",
                last_reopen_reason: data.reason || trimmed,
            };
            setState(next);
            _emit(next);
            refreshValidity(next);
            setReopenDialogOpen(false);
            setReason("");
            toast.success(i18n.t("wf.periodCloseControl.periodReopened", "Period reopened"));
        } catch (err) {
            const status = err && err.response
                ? err.response.status : undefined;
            const detail = err && err.response && err.response.data
                ? err.response.data.detail : undefined;
            const code = detail && typeof detail === "object"
                ? detail.code : undefined;
            if (status === 409 && code === "WORKFORCE_PERIOD_NOT_CLOSED") {
                toast.error(i18n.t("wf.PeriodCloseControl._MSG_ALREADY_OPEN", _MSG_ALREADY_OPEN));
                setReopenDialogOpen(false);
                await load();
            } else if (status === 503
                    && code === "WORKFORCE_REOPEN_TRANSACTION_UNAVAILABLE") {
                toast.error(i18n.t("wf.PeriodCloseControl._MSG_REOPEN_UNAVAILABLE", _MSG_REOPEN_UNAVAILABLE));
            } else {
                toast.error(i18n.t("wf.PeriodCloseControl._MSG_REOPEN_GENERIC", _MSG_REOPEN_GENERIC));
            }
        } finally {
            setBusy(false);
        }
    }, [restaurantId, periodId, busy, reason, load, _emit, refreshValidity]);

    if (!authorized) return null;
    if (!periodId) return null;
    if (loading && !state) {
        return (
            <span
                data-testid="workforce-close-status"
                data-close-state="LOADING"
                className="text-sm text-neutral-400"
            >
                …
            </span>
        );
    }
    if (!state) return null;

    const closed = _isClosed(state);
    const history = _hasHistory(state);
    const closedAt = closed ? _formatClosedAt(state.closed_at) : "";
    const closeLabel = history && !closed ? i18n.t("wf.PeriodCloseControl._CLOSE_AGAIN_CTA", _CLOSE_AGAIN_CTA) : i18n.t("wf.PeriodCloseControl._CLOSE_CTA", _CLOSE_CTA);
    const trimmedReason = (reason || "").trim();
    const reasonValid = trimmedReason.length >= _REASON_MIN
        && trimmedReason.length <= _REASON_MAX;

    return (
        <span
            data-testid="workforce-close-control"
            data-close-state={closed ? "CLOSED" : "OPEN"}
            data-close-has-history={history ? "true" : "false"}
            className="inline-flex items-center gap-2"
        >
            {closed ? (
                <>
                    <span
                        data-testid="workforce-close-status"
                        data-close-state="CLOSED"
                        className="inline-flex items-center gap-1
                                    rounded-full border border-neutral-300
                                    bg-neutral-50 px-2 py-0.5 text-xs
                                    text-neutral-700"
                    >
                        {i18n.t("wf.periodCloseControl.closed", "Closed")}
                        <span
                            data-testid="workforce-close-version"
                            className="text-neutral-500"
                        >
                            v{state.latest_close_version}
                        </span>
                        {closedAt && (
                            <span
                                data-testid="workforce-close-closed-at"
                                className="text-neutral-400"
                            >
                                · {closedAt}
                            </span>
                        )}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                            setReason("");
                            setReopenDialogOpen(true);
                        }}
                        disabled={busy}
                        data-testid="workforce-close-reopen-btn"
                    >
                        {i18n.t("wf.PeriodCloseControl._REOPEN_CTA", _REOPEN_CTA)}
                    </Button>
                </>
            ) : (
                <>
                    {history && (
                        <span
                            data-testid="workforce-close-status"
                            data-close-state="REOPENED"
                            className="inline-flex items-center gap-1
                                        rounded-full border border-neutral-300
                                        bg-white px-2 py-0.5 text-xs
                                        text-neutral-700"
                        >
                            {i18n.t("wf.periodCloseControl.reopened", "Reopened")}
                            <span
                                data-testid="workforce-close-version"
                                className="text-neutral-500"
                            >
                                (previous v{state.latest_close_version})
                            </span>
                        </span>
                    )}
                    {!history && (
                        <span
                            data-testid="workforce-close-status"
                            data-close-state="OPEN"
                            className="text-xs text-neutral-500"
                        >
                            {i18n.t("wf.periodCloseControl.open", "Open")}
                        </span>
                    )}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setCloseDialogOpen(true)}
                        disabled={busy}
                        data-testid="workforce-close-btn"
                    >
                        {closeLabel}
                    </Button>
                </>
            )}

            {/* §4 — Close confirmation dialog. */}
            <AlertDialog
                open={closeDialogOpen}
                onOpenChange={setCloseDialogOpen}
            >
                <AlertDialogContent
                    data-testid="workforce-close-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            data-testid="workforce-close-dialog-title"
                        >
                            {i18n.t("wf.PeriodCloseControl._CLOSE_DIALOG_TITLE", _CLOSE_DIALOG_TITLE)}
                        </AlertDialogTitle>
                        <AlertDialogDescription
                            data-testid="workforce-close-dialog-body"
                        >
                            {i18n.t("wf.PeriodCloseControl._CLOSE_DIALOG_BODY", _CLOSE_DIALOG_BODY)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setCloseDialogOpen(false)}
                            disabled={busy}
                            data-testid="workforce-close-dialog-cancel"
                        >
                            {i18n.t("wf.PeriodCloseControl._CLOSE_DIALOG_CANCEL", _CLOSE_DIALOG_CANCEL)}
                        </Button>
                        <Button
                            onClick={doClose}
                            disabled={busy}
                            data-testid="workforce-close-dialog-confirm"
                        >
                            {i18n.t("wf.PeriodCloseControl._CLOSE_DIALOG_CONFIRM", _CLOSE_DIALOG_CONFIRM)}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {/* §9 — Reopen reason dialog. */}
            <AlertDialog
                open={reopenDialogOpen}
                onOpenChange={(next) => {
                    setReopenDialogOpen(next);
                    if (!next) setReason("");
                }}
            >
                <AlertDialogContent
                    data-testid="workforce-reopen-dialog"
                >
                    <AlertDialogHeader>
                        <AlertDialogTitle
                            data-testid="workforce-reopen-dialog-title"
                        >
                            {i18n.t("wf.PeriodCloseControl._REOPEN_DIALOG_TITLE", _REOPEN_DIALOG_TITLE)}
                        </AlertDialogTitle>
                        <AlertDialogDescription
                            data-testid="workforce-reopen-dialog-body"
                        >
                            {i18n.t("wf.PeriodCloseControl._REOPEN_DIALOG_BODY", _REOPEN_DIALOG_BODY)}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <label
                        htmlFor="workforce-reopen-reason"
                        className="text-xs font-medium text-neutral-600"
                        data-testid="workforce-reopen-reason-label"
                    >
                        {i18n.t("wf.PeriodCloseControl._REOPEN_REASON_LABEL", _REOPEN_REASON_LABEL)}
                    </label>
                    <Textarea
                        id="workforce-reopen-reason"
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        rows={3}
                        maxLength={_REASON_MAX}
                        disabled={busy}
                        data-testid="workforce-reopen-reason-input"
                        placeholder={i18n.t("wf.periodCloseControl.whyIsThisPeriodBeingReopened", "Why is this period being reopened?")}
                    />
                    <AlertDialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => {
                                setReopenDialogOpen(false);
                                setReason("");
                            }}
                            disabled={busy}
                            data-testid="workforce-reopen-dialog-cancel"
                        >
                            {i18n.t("wf.PeriodCloseControl._REOPEN_DIALOG_CANCEL", _REOPEN_DIALOG_CANCEL)}
                        </Button>
                        <Button
                            onClick={doReopen}
                            disabled={busy || !reasonValid}
                            data-testid="workforce-reopen-dialog-confirm"
                        >
                            {i18n.t("wf.PeriodCloseControl._REOPEN_DIALOG_CONFIRM", _REOPEN_DIALOG_CONFIRM)}
                        </Button>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </span>
    );
}
